// VIDEO-EDITING-MVP-OVERLAYS — shared TypeScript shape for one graphical
// pop-up overlay (callout box, hook pill, lower-third, sticker image) +
// curated templates surfaced in the "+ Add" popover.
//
// Persisted under `metadata.overlays[]` on the project Artifact. The
// matching zod validator on the Core side lives at
// `core/src/platform/providers/overlays/schema.ts` — keep them in lockstep.
import { z } from "zod";

export type OverlayKind =
  | "text-box"
  | "pill-hook"
  | "lower-third"
  | "sticker-image";

export type OverlayAnim =
  | "none"
  | "fade"
  | "pop"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right";

export type OverlayAnchor =
  | "tl"
  | "tc"
  | "tr"
  | "ml"
  | "mc"
  | "mr"
  | "bl"
  | "bc"
  | "br";

export type OverlaySourceKind =
  | "manual"
  | "generated"
  | "clone-source"
  | "upload"
  | "image-asset";

// Phase G — must mirror OVERLAY_FONT_FAMILIES in
// core/src/platform/providers/overlays/schema.ts exactly. Adding a value
// here without bundling the matching TTF in core/Dockerfile yields a
// silent libass fallback at burn time.
export const OVERLAY_FONT_FAMILIES = [
  "Inter",
  "Vazirmatn",
  "Cairo",
  "Noto Sans Hebrew",
  "Noto Sans CJK",
  "Bebas Neue",
  "Anton",
  "Archivo Black",
  "Alfa Slab One",
  "Abril Fatface",
  "Bungee",
  "Oswald",
  "Montserrat",
  "Poppins",
  "Raleway",
  "DM Sans",
  "Playfair Display",
  "Merriweather",
  "Lora",
  "Roboto Slab",
  "DM Serif Display",
  "Lobster",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Permanent Marker",
  "Bangers",
  "Comic Neue",
  "Fredoka",
  "JetBrains Mono",
] as const;

export type OverlayFontFamily = (typeof OVERLAY_FONT_FAMILIES)[number];

export interface OverlayStyle {
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  fontWeight?: number;
  fontFamily?: OverlayFontFamily;
  textAlign?: "left" | "center" | "right";
  paddingX?: number;
  paddingY?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  uppercase?: boolean;
  letterSpacing?: number;
  rotation?: number;
  opacity?: number;
  shadow?: {
    dx: number;
    dy: number;
    blur: number;
    color: string;
  };
}

export const MIN_OVERLAY_FONT_SIZE = 0.02;
export const MAX_OVERLAY_FONT_SIZE = 0.2;

export function clampOverlayFontSize(n: number): number {
  if (!Number.isFinite(n)) return 0.05;
  return Math.max(MIN_OVERLAY_FONT_SIZE, Math.min(MAX_OVERLAY_FONT_SIZE, n));
}

export function normalizeOverlayFontSize(n: number): number {
  const scaled = n > 1 ? n / 1000 : n;
  return clampOverlayFontSize(scaled);
}

export function normalizeOverlayStyle(
  style: OverlayStyle | undefined,
): OverlayStyle | undefined {
  if (!style) return undefined;
  return {
    ...style,
    ...(style.fontSize != null
      ? { fontSize: normalizeOverlayFontSize(style.fontSize) }
      : {}),
  };
}

export function normalizeOverlay(overlay: Overlay): Overlay {
  return {
    ...overlay,
    ...(overlay.style ? { style: normalizeOverlayStyle(overlay.style) } : {}),
  };
}

export function normalizeOverlays(overlays: Overlay[]): Overlay[] {
  return overlays.map(normalizeOverlay);
}

export interface OverlayAsset {
  url: string;
  storageKey: string;
  mimeType: string;
  w: number;
  h: number;
}

export interface OverlayMotionPoint {
  timeSec: number;
  x: number;
  y: number;
  scale?: number;
  opacity?: number;
}

export interface OverlaySource {
  kind?: OverlaySourceKind;
  actionId?: string;
  managed?: boolean;
  cloneSourceArtifactId?: string;
  sourceTrackId?: string;
  confidence?: number;
}

export interface Overlay {
  id: string;
  kind: OverlayKind;
  role?: string;
  lane?: string;
  x: number;
  y: number;
  w: number;
  h: number;
  anchor: OverlayAnchor;
  startSec: number;
  endSec: number;
  text?: string;
  style?: OverlayStyle;
  asset?: OverlayAsset;
  animIn?: OverlayAnim;
  animOut?: OverlayAnim;
  animDurMs?: number;
  motionPath?: OverlayMotionPoint[];
  source?: OverlaySource;
}

export const OVERLAY_ANIM_DEFAULT_MS = 250;

export interface OverlayTranslationEntry {
  id: string;
  text: string;
}

export interface OverlayTranslation {
  language: string;
  overlays: OverlayTranslationEntry[];
}

// Zod boundary schemas — used to validate VideoProject network responses.
// We keep the runtime types above as plain interfaces because the editor
// passes them through Remotion + canvas math; the Zod parser only runs at
// fetch/hydrate boundaries.
const OverlayKindEnum = z.enum([
  "text-box",
  "pill-hook",
  "lower-third",
  "sticker-image",
]);

const OverlayAnimEnum = z.enum([
  "none",
  "fade",
  "pop",
  "slide-up",
  "slide-down",
  "slide-left",
  "slide-right",
]);

const OverlayAnchorEnum = z.enum([
  "tl",
  "tc",
  "tr",
  "ml",
  "mc",
  "mr",
  "bl",
  "bc",
  "br",
]);

const OverlaySourceKindEnum = z.enum([
  "manual",
  "generated",
  "clone-source",
  "upload",
  "image-asset",
]);

const OverlayStyleSchema: z.ZodType<OverlayStyle> = z
  .object({
    color: z.string().optional(),
    backgroundColor: z.string().optional(),
    fontSize: z
      .preprocess(
        (value) =>
          typeof value === "number" ? normalizeOverlayFontSize(value) : value,
        z.number(),
      )
      .optional(),
    fontWeight: z.number().optional(),
    fontFamily: z.enum(OVERLAY_FONT_FAMILIES).optional(),
    textAlign: z.enum(["left", "center", "right"]).optional(),
    paddingX: z.number().optional(),
    paddingY: z.number().optional(),
    borderRadius: z.number().optional(),
    borderWidth: z.number().optional(),
    borderColor: z.string().optional(),
    uppercase: z.boolean().optional(),
    letterSpacing: z.number().optional(),
    rotation: z.number().optional(),
    opacity: z.number().optional(),
    shadow: z
      .object({
        dx: z.number(),
        dy: z.number(),
        blur: z.number(),
        color: z.string(),
      })
      .optional(),
  })
  .passthrough();

const OverlayAssetSchema: z.ZodType<OverlayAsset> = z.object({
  url: z.string(),
  storageKey: z.string(),
  mimeType: z.string(),
  w: z.number(),
  h: z.number(),
});

const OverlayMotionPointSchema: z.ZodType<OverlayMotionPoint> = z.object({
  timeSec: z.number(),
  x: z.number(),
  y: z.number(),
  scale: z.number().optional(),
  opacity: z.number().optional(),
});

const OverlaySourceSchema: z.ZodType<OverlaySource> = z
  .object({
    kind: OverlaySourceKindEnum.optional(),
    actionId: z.string().optional(),
    managed: z.boolean().optional(),
    cloneSourceArtifactId: z.string().optional(),
    sourceTrackId: z.string().optional(),
    confidence: z.number().optional(),
  })
  .passthrough();

export const OverlaySchema: z.ZodType<Overlay> = z.object({
  id: z.string(),
  kind: OverlayKindEnum,
  role: z.string().optional(),
  lane: z.string().optional(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  anchor: OverlayAnchorEnum,
  startSec: z.number(),
  endSec: z.number(),
  text: z.string().optional(),
  style: OverlayStyleSchema.optional(),
  asset: OverlayAssetSchema.optional(),
  animIn: OverlayAnimEnum.optional(),
  animOut: OverlayAnimEnum.optional(),
  animDurMs: z.number().optional(),
  motionPath: z.array(OverlayMotionPointSchema).optional(),
  source: OverlaySourceSchema.optional(),
});

export const OverlayTranslationSchema = z.object({
  language: z.string(),
  overlays: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
    }),
  ),
});

// ── Templates ────────────────────────────────────────────────────────────
export interface OverlayTemplate {
  id: string;
  name: string;
  hint: string;
  partial: Omit<Overlay, "id" | "startSec" | "endSec">;
}

export const OVERLAY_DEFAULT_DURATION_SEC = 3;

export const OVERLAY_TEMPLATES: OverlayTemplate[] = [
  {
    id: "blank-text",
    name: "Blank text",
    hint: "Empty white text box — start from scratch",
    partial: {
      kind: "text-box",
      x: 0.5,
      y: 0.5,
      w: 0.5,
      h: 0.1,
      anchor: "mc",
      text: "Your text here",
      style: {
        color: "#FFFFFF",
        fontSize: 0.06,
        fontWeight: 700,
        fontFamily: "Inter",
        textAlign: "center",
      },
      animIn: "fade",
      animOut: "fade",
    },
  },
  {
    id: "bright-pill-hook",
    name: "Bright pill hook",
    hint: "Stop-scrolling style — bold yellow pill",
    partial: {
      kind: "pill-hook",
      x: 0.5,
      y: 0.18,
      w: 0.7,
      h: 0.1,
      anchor: "mc",
      text: "Stop scrolling 👀",
      style: {
        color: "#0A0A0A",
        backgroundColor: "#FFD400",
        fontSize: 0.06,
        fontWeight: 900,
        fontFamily: "Inter",
        textAlign: "center",
        paddingX: 18,
        paddingY: 10,
        borderRadius: 999,
        uppercase: true,
        letterSpacing: 0.03,
      },
      animIn: "pop",
      animOut: "fade",
    },
  },
  {
    id: "lower-third",
    name: "Lower-third name tag",
    hint: "Speaker / location tag along the bottom band",
    partial: {
      kind: "lower-third",
      x: 0.06,
      y: 0.78,
      w: 0.55,
      h: 0.1,
      anchor: "bl",
      text: "Kazz · CEO",
      style: {
        color: "#FFFFFF",
        backgroundColor: "rgba(0,0,0,0.7)",
        fontSize: 0.045,
        fontWeight: 700,
        fontFamily: "Inter",
        textAlign: "left",
        paddingX: 14,
        paddingY: 8,
        borderRadius: 8,
      },
      animIn: "slide-right",
      animOut: "fade",
    },
  },
  {
    id: "trust-badge",
    name: "Trust badge",
    hint: "Small high-contrast badge in a corner",
    partial: {
      kind: "pill-hook",
      x: 0.94,
      y: 0.08,
      w: 0.32,
      h: 0.08,
      anchor: "tr",
      text: "★ 4.9 / 5",
      style: {
        color: "#0A0A0A",
        backgroundColor: "#FFFFFF",
        fontSize: 0.038,
        fontWeight: 800,
        fontFamily: "Inter",
        textAlign: "center",
        paddingX: 12,
        paddingY: 6,
        borderRadius: 12,
      },
      animIn: "fade",
      animOut: "fade",
    },
  },
  {
    id: "stat-callout",
    name: "Stat callout",
    hint: "Punchy stat in big bold type",
    partial: {
      kind: "text-box",
      x: 0.5,
      y: 0.5,
      w: 0.72,
      h: 0.22,
      anchor: "mc",
      text: "+312%",
      style: {
        color: "#FFD400",
        fontSize: 0.16,
        fontWeight: 900,
        fontFamily: "Inter",
        textAlign: "center",
        uppercase: true,
        shadow: { dx: 0, dy: 2, blur: 12, color: "rgba(0,0,0,0.5)" },
      },
      animIn: "pop",
      animOut: "fade",
    },
  },
  {
    id: "cta-bubble",
    name: "CTA bubble",
    hint: "Action prompt in a rounded high-contrast bubble",
    partial: {
      kind: "pill-hook",
      x: 0.5,
      y: 0.8,
      w: 0.6,
      h: 0.1,
      anchor: "bc",
      text: "Try free →",
      style: {
        color: "#FFFFFF",
        backgroundColor: "#D9381F",
        fontSize: 0.05,
        fontWeight: 900,
        fontFamily: "Inter",
        textAlign: "center",
        paddingX: 18,
        paddingY: 10,
        borderRadius: 999,
        uppercase: true,
      },
      animIn: "slide-up",
      animOut: "fade",
    },
  },
  {
    id: "pull-quote",
    name: "Pull-quote card",
    hint: "Quote card in a soft dark box",
    partial: {
      kind: "text-box",
      x: 0.5,
      y: 0.5,
      w: 0.7,
      h: 0.3,
      anchor: "mc",
      text: '"This changed how we ship."',
      style: {
        color: "#F5F0E6",
        backgroundColor: "rgba(0,0,0,0.55)",
        fontSize: 0.05,
        fontWeight: 600,
        fontFamily: "Inter",
        textAlign: "center",
        paddingX: 18,
        paddingY: 14,
        borderRadius: 12,
      },
      animIn: "fade",
      animOut: "fade",
    },
  },
];
